import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FINANCING_KIND_LABELS, PROJECT_STATUSES, PROJECT_STATUS_LABELS, formatCents, type CreateProjectInput, type FinancingProject, type FinancingProjectDetail, type Paginated, type ProjectStatus } from '@kontor/shared';
import { api, queryString } from '../../api';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { FinancingTabs } from './FinancingTabs';
import { ProjectForm } from './ProjectForm';
import { errorText, formatDay } from './shared';
import '../fleet/fleet.css';
import './financing.css';

export const STATUS_BADGE: Record<ProjectStatus, string> = { planned: 'fleet-badge--info', submitted: 'fleet-badge--warn', approved: 'fleet-badge--ok', rejected: 'fleet-badge--bad' };

/**
 * Finanzierungsvorhaben (KON-256): geplante Kredite, Ratenkäufe und Leasingverträge mit Status geplant, eingereicht, bewilligt oder abgelehnt. Jedes Vorhaben rechnet seine Rate, prüft die
 * Tragfähigkeit und erzeugt die Mappe für die Bank. Aus einem bewilligten Vorhaben entsteht der Vertrag.
 */
export function ProjectsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['financing-projects', { q, status, page }],
    queryFn: () => api<Paginated<FinancingProject>>('GET', `/financing-projects${queryString({ q, status, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [{ key: 'status', label: 'Stand', type: 'select', options: [{ value: '', label: 'Alle' }, ...PROJECT_STATUSES.map((s) => ({ value: s, label: PROJECT_STATUS_LABELS[s] }))] }];
  const create = useMutation({
    mutationFn: async (body: CreateProjectInput) => (await api<{ data: FinancingProjectDetail }>('POST', '/financing-projects', body)).data,
    onSuccess: (created) => {
      client.invalidateQueries({ queryKey: ['financing-projects'] });
      navigate(`/finanzierungen/vorhaben/${created.id}`);
    },
    onError: (e) => setError(errorText(e, 'Das Vorhaben konnte nicht angelegt werden.')),
  });

  return (
    <>
      <FinancingTabs />
      <div className="page-head">
        <h1>Finanzierungsvorhaben</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} Vorhaben` : ''}</span>
        {can('financing.write') && <button type="button" className="btn btn--primary" onClick={() => { setError(null); setCreating(!creating); }}>Neues Vorhaben</button>}
      </div>
      <p className="muted">Planen Sie hier einen neuen Kredit, Ratenkauf oder Leasingvertrag, bevor Sie ihn abschließen. Wirkia prüft aus Ihren Rechnungen, Löhnen und festen Kosten, ob die Rate getragen wird, und erzeugt die Mappe für Bank oder Leasinggesellschaft. Ist das Vorhaben bewilligt, legen Sie daraus mit einem Klick den Vertrag an.</p>

      {creating && <ProjectForm onSubmit={(b) => { setError(null); create.mutate(b); }} onCancel={() => setCreating(false)} pending={create.isPending} error={error} />}

      <FilterBar search={{ value: q, placeholder: 'Bezeichnung, Nummer oder Geldgeber', onSubmit: (v) => update({ q: v }) }} fields={fields} values={{ status }} onChange={(k, v) => update({ [k]: v })} onReset={() => setParams(new URLSearchParams())} />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards">
          <thead>
            <tr><th>Vorhaben</th><th>Art</th><th>Beantragt bei</th><th className="num">Rate</th><th>Laufzeit</th><th>Eingereicht</th><th>Stand</th></tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={7} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={7} className="empty">Noch keine Vorhaben. Legen Sie mit „Neues Vorhaben“ an, was Sie finanzieren möchten.</td></tr>}
            {list.data?.data.map((p) => (
              <ExpandRow key={p.id} summary={<Summary title={<Link to={`/finanzierungen/vorhaben/${p.id}`} className="strong-link">{p.title}</Link>} aside={<strong>{p.monthlyCents !== null ? formatCents(p.monthlyCents) : '–'}</strong>} line2={`${p.number} · ${FINANCING_KIND_LABELS[p.kind]} · ${PROJECT_STATUS_LABELS[p.status]}`} />}>
                <td className="cell-desktop-title"><Link to={`/finanzierungen/vorhaben/${p.id}`} className="strong-link">{p.title}</Link><div className="muted small">{p.number}</div></td>
                <td data-label="Art">{FINANCING_KIND_LABELS[p.kind]}</td>
                <td data-label="Beantragt bei">{p.lenderName ?? '–'}</td>
                <td data-label="Rate" className="num">{p.monthlyCents !== null ? formatCents(p.monthlyCents) : '–'}</td>
                <td data-label="Laufzeit">{p.termMonths} Monate</td>
                <td data-label="Eingereicht">{formatDay(p.submittedOn)}</td>
                <td data-label="Stand"><span className={`fleet-badge ${STATUS_BADGE[p.status]}`}>{PROJECT_STATUS_LABELS[p.status]}</span></td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <div className="pager">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </div>
      )}
    </>
  );
}
